import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { LogOut, Trash2, Loader2, FileText, Star, Images, Clock, Plus, ChevronDown, HardHat } from "lucide-react";
import { API, GALLERY_CATEGORIES, gallerySrc, videoSrc } from "@/config/site";
import { usePageTitle, Eyebrow } from "@/components/bits";

const authCfg = { withCredentials: true };
const TABS = [
  { id: "quotes", label: "Quote Requests", icon: FileText },
  { id: "applications", label: "Job Applications", icon: HardHat },
  { id: "jobs", label: "Careers", icon: Clock },
  { id: "testimonials", label: "Testimonials", icon: Star },
  { id: "gallery", label: "Our Work", icon: Images },
];

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, authCfg);
      onLogin(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-joell-black px-5">
      <form onSubmit={submit} className="w-full max-w-md border border-white/10 bg-joell-surface p-8 sm:p-10" data-testid="admin-login-form">
        <img src="/logo.png" alt="JOELL" className="h-14 w-auto" />
        <h1 className="mt-6 font-display text-2xl font-bold uppercase text-white">Owner Dashboard</h1>
        <p className="mt-2 text-sm text-slate-400">Sign in to manage quotes, applications, testimonials, photos and hours.</p>
        <label htmlFor="admin-email" className="field-label mt-8">Email</label>
        <input id="admin-email" type="email" className="input-dark" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" />
        <label htmlFor="admin-password" className="field-label mt-4">Password</label>
        <input id="admin-password" type="password" className="input-dark" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password" />
        {error && <p className="mt-4 border-l-2 border-red-500 bg-red-500/10 px-4 py-3 text-sm text-red-300" role="alert" data-testid="admin-login-error">{error}</p>}
        <button type="submit" disabled={busy} className="btn-accent mt-6 w-full !py-4" data-testid="admin-login-submit">
          {busy ? <Loader2 size={16} className="animate-spin" /> : "Sign In"}
        </button>
      </form>
    </div>
  );
}

function QuotesTab() {
  const qc = useQueryClient();
  const { data: quotes = [], isLoading } = useQuery({
    queryKey: ["admin-quotes"],
    queryFn: async () => (await axios.get(`${API}/quotes`, authCfg)).data,
  });

  const setStatus = async (id, status) => {
    await axios.patch(`${API}/quotes/${id}`, { status }, authCfg);
    qc.invalidateQueries({ queryKey: ["admin-quotes"] });
  };

  const removeQuote = async (id) => {
    await axios.delete(`${API}/quotes/${id}`, authCfg);
    qc.invalidateQueries({ queryKey: ["admin-quotes"] });
  };

  if (isLoading) return <p className="mt-8 font-mono text-xs uppercase tracking-widest text-slate-500">Loading…</p>;
  if (quotes.length === 0)
    return <p className="mt-8 border border-dashed border-white/20 p-10 text-center font-mono text-xs uppercase tracking-widest text-slate-500" data-testid="admin-quotes-empty">No quote requests yet</p>;

  return (
    <div className="mt-8 space-y-4">
      {quotes.map((q) => (
        <div key={q.id} className="border border-white/10 bg-joell-surface p-6" data-testid={`admin-quote-card-${q.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-display text-lg font-bold uppercase text-white">{q.name}</h3>
            <select
              value={q.status || "new"}
              onChange={(e) => setStatus(q.id, e.target.value)}
              className={`border bg-transparent px-2.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest ${
                q.status === "won" ? "border-joell-yellow text-joell-yellow" :
                q.status === "quoted" ? "border-sky-400 text-sky-400" :
                q.status === "lost" ? "border-slate-500 text-slate-400" :
                "border-joell-green text-joell-green"
              }`}
              aria-label={`Status for quote from ${q.name}`}
              data-testid={`admin-quote-status-${q.id}`}
            >
              {["new", "quoted", "won", "lost"].map((s) => <option key={s} value={s} className="bg-joell-coal text-white">{s}</option>)}
            </select>
            <button onClick={() => removeQuote(q.id)} className="flex h-9 w-9 items-center justify-center border border-white/15 text-slate-400 transition-colors hover:border-red-500 hover:text-red-400" aria-label={`Delete quote request from ${q.name}`} data-testid={`admin-quote-delete-${q.id}`}>
              <Trash2 size={15} />
            </button>
          </div>
          <div className="mt-3 grid gap-x-8 gap-y-1 text-sm text-slate-400 sm:grid-cols-2">
            <p><span className="text-slate-500">Phone:</span> <a href={`tel:${q.phone}`} className="text-white">{q.phone}</a></p>
            {q.email && <p><span className="text-slate-500">Email:</span> <a href={`mailto:${q.email}`} className="break-all text-white">{q.email}</a></p>}
            <p><span className="text-slate-500">Location:</span> <span className="text-white">{q.location}</span></p>
            <p><span className="text-slate-500">Service:</span> <span className="text-white">{q.service}</span></p>
            <p><span className="text-slate-500">Contact by:</span> <span className="text-white">{q.contact_method}</span>{q.contact_time && <> · <span className="text-white">{q.contact_time}</span></>}</p>
            <p><span className="text-slate-500">Received:</span> <span className="text-white">{new Date(q.created_at).toLocaleString()}</span></p>
          </div>
          {q.message && <p className="mt-3 border-l-2 border-white/15 pl-4 text-sm text-slate-300">{q.message}</p>}
          {q.photo_paths?.length > 0 && (
            <div className="mt-4 grid grid-cols-5 gap-2">
              {q.photo_paths.map((p) => (
                <a key={p} href={`${API}/files/${p}`} target="_blank" rel="noreferrer" className="block aspect-square overflow-hidden border border-white/10">
                  <img src={`${API}/files/${p}`} alt="Customer upload" className="h-full w-full object-cover" loading="lazy" />
                </a>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ApplicationsTab() {
  const qc = useQueryClient();
  const { data: apps = [], isLoading } = useQuery({
    queryKey: ["admin-applications"],
    queryFn: async () => (await axios.get(`${API}/applications`, authCfg)).data,
  });

  const remove = async (id) => {
    await axios.delete(`${API}/applications/${id}`, authCfg);
    qc.invalidateQueries({ queryKey: ["admin-applications"] });
  };

  if (isLoading) return <p className="mt-8 font-mono text-xs uppercase tracking-widest text-slate-500">Loading…</p>;
  if (apps.length === 0)
    return <p className="mt-8 border border-dashed border-white/20 p-10 text-center font-mono text-xs uppercase tracking-widest text-slate-500" data-testid="admin-applications-empty">No applications yet</p>;

  return (
    <div className="mt-8 space-y-4">
      {apps.map((a) => (
        <div key={a.id} className="border border-white/10 bg-joell-surface p-6" data-testid={`admin-application-${a.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-display text-lg font-bold uppercase text-white">{a.name}</h3>
            <span className="bg-joell-yellow/10 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-joell-yellow">{a.position}</span>
          </div>
          <div className="mt-3 grid gap-x-8 gap-y-1 text-sm text-slate-400 sm:grid-cols-2">
            <p><span className="text-slate-500">Phone:</span> <a href={`tel:${a.phone}`} className="text-white">{a.phone}</a></p>
            <p><span className="text-slate-500">Email:</span> <a href={`mailto:${a.email}`} className="break-all text-white">{a.email}</a></p>
            <p><span className="text-slate-500">Experience:</span> <span className="text-white">{a.years_experience} years</span></p>
            <p><span className="text-slate-500">Applied:</span> <span className="text-white">{new Date(a.created_at).toLocaleString()}</span></p>
          </div>
          {a.message && <p className="mt-3 border-l-2 border-white/15 pl-4 text-sm text-slate-300">{a.message}</p>}
          <div className="mt-4 flex items-center gap-3">
            {a.cv_path && (
              <a href={`${API}/files/${a.cv_path}`} target="_blank" rel="noreferrer" className="btn-outline !px-4 !py-2 !text-[10px]" data-testid={`admin-application-cv-${a.id}`}>
                View CV (PDF)
              </a>
            )}
            <button onClick={() => remove(a.id)} className="flex h-9 w-9 items-center justify-center border border-white/15 text-slate-400 transition-colors hover:border-red-500 hover:text-red-400" aria-label={`Delete application from ${a.name}`} data-testid={`admin-application-delete-${a.id}`}>
              <Trash2 size={15} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function JobsTab() {
  const qc = useQueryClient();
  const { data: jobs = [] } = useQuery({
    queryKey: ["admin-jobs"],
    queryFn: async () => (await axios.get(`${API}/admin/jobs`, authCfg)).data,
  });
  const emptyForm = { title: "", job_type: "Full-time", openings: 1, summary: "", requirements: "", responsibilities: "" };
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const startEdit = (j) => {
    setEditingId(j.id);
    setForm({
      title: j.title,
      job_type: j.job_type || "Full-time",
      openings: j.openings || 1,
      summary: j.summary || "",
      requirements: (j.requirements || []).join("\n"),
      responsibilities: (j.responsibilities || []).join("\n"),
    });
  };
  const cancelEdit = () => { setEditingId(null); setForm(emptyForm); };

  const add = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const body = {
        title: form.title,
        job_type: form.job_type,
        openings: Number(form.openings) || 1,
        summary: form.summary,
        requirements: form.requirements.split("\n").map((x) => x.trim()).filter(Boolean),
        responsibilities: form.responsibilities.split("\n").map((x) => x.trim()).filter(Boolean),
      };
      if (editingId) await axios.put(`${API}/jobs/${editingId}`, body, authCfg);
      else await axios.post(`${API}/jobs`, body, authCfg);
      setForm(emptyForm);
      setEditingId(null);
      qc.invalidateQueries({ queryKey: ["admin-jobs"] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
    } catch (err) {
      setError("Could not save — check the fields and try again.");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (id, active) => {
    await axios.patch(`${API}/jobs/${id}?active=${active}`, {}, authCfg);
    qc.invalidateQueries({ queryKey: ["admin-jobs"] });
    qc.invalidateQueries({ queryKey: ["jobs"] });
  };

  const remove = async (id) => {
    await axios.delete(`${API}/jobs/${id}`, authCfg);
    qc.invalidateQueries({ queryKey: ["admin-jobs"] });
    qc.invalidateQueries({ queryKey: ["jobs"] });
  };

  return (
    <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_1.3fr]">
      <form onSubmit={add} className="h-fit border border-white/10 bg-joell-surface p-6" data-testid="admin-job-form">
        <h3 className="font-display text-lg font-bold uppercase text-white">{editingId ? "Edit listing" : "Add a job listing"}</h3>
        <label className="field-label mt-4">Job title *</label>
        <input className="input-dark" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-job-title" />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="field-label mt-4">Type</label>
            <input className="input-dark" value={form.job_type} onChange={(e) => setForm({ ...form, job_type: e.target.value })} data-testid="admin-job-type" />
          </div>
          <div>
            <label className="field-label mt-4">Openings</label>
            <input type="number" min="1" className="input-dark" value={form.openings} onChange={(e) => setForm({ ...form, openings: e.target.value })} data-testid="admin-job-openings" />
          </div>
        </div>
        <label className="field-label mt-4">Short summary *</label>
        <textarea rows={3} className="input-dark resize-y" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="admin-job-summary" />
        <label className="field-label mt-4">Requirements (one per line)</label>
        <textarea rows={4} className="input-dark resize-y" value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} data-testid="admin-job-requirements" />
        <label className="field-label mt-4">The work (one per line)</label>
        <textarea rows={4} className="input-dark resize-y" value={form.responsibilities} onChange={(e) => setForm({ ...form, responsibilities: e.target.value })} data-testid="admin-job-responsibilities" />
        {error && <p className="mt-4 text-sm text-red-300" role="alert">{error}</p>}
        <div className="mt-6 flex gap-3">
          <button type="submit" disabled={busy} className="btn-accent flex-1" data-testid="admin-job-submit">
            {busy ? <Loader2 size={15} className="animate-spin" /> : editingId ? "Save Changes" : "Add Listing"}
          </button>
          {editingId && (
            <button type="button" onClick={cancelEdit} className="btn-outline flex-1" data-testid="admin-job-cancel">
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="space-y-3">
        {jobs.map((j) => (
          <div key={j.id} className="flex items-center justify-between gap-4 border border-white/10 bg-joell-surface p-5" data-testid={`admin-job-${j.id}`}>
            <div className="min-w-0">
              <p className="font-display text-base font-bold uppercase text-white">{j.title}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{j.job_type} · {j.openings} opening{j.openings > 1 ? "s" : ""}</p>
              <button
                onClick={() => startEdit(j)}
                className="mt-2 mr-2 border border-white/15 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-slate-300 transition-colors hover:border-joell-yellow hover:text-joell-yellow"
                data-testid={`admin-job-edit-${j.id}`}
              >
                Edit
              </button>
              <button
                onClick={() => toggle(j.id, !j.active)}
                className={`mt-2 border px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest ${j.active ? "border-joell-green text-joell-green" : "border-slate-500 text-slate-400"}`}
                data-testid={`admin-job-toggle-${j.id}`}
              >
                {j.active ? "Open — click to close" : "Closed — click to reopen"}
              </button>
            </div>
            <button onClick={() => remove(j.id)} className="flex h-9 w-9 shrink-0 items-center justify-center border border-white/15 text-slate-400 transition-colors hover:border-red-500 hover:text-red-400" aria-label={`Delete job ${j.title}`} data-testid={`admin-job-delete-${j.id}`}>
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function TestimonialsTab() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: "", location: "", text: "", rating: 5 });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: items = [] } = useQuery({
    queryKey: ["testimonials"],
    queryFn: async () => (await axios.get(`${API}/testimonials`)).data,
  });

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/testimonials`, { ...form, rating: Number(form.rating) }, authCfg);
      setForm({ name: "", location: "", text: "", rating: 5 });
      qc.invalidateQueries({ queryKey: ["testimonials"] });
    } catch (err) {
      setError("Could not save — check the fields and try again.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await axios.delete(`${API}/testimonials/${id}`, authCfg);
    qc.invalidateQueries({ queryKey: ["testimonials"] });
  };

  return (
    <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_1.3fr]">
      <form onSubmit={add} className="h-fit border border-white/10 bg-joell-surface p-6" data-testid="admin-testimonial-form">
        <h3 className="font-display text-lg font-bold uppercase text-white">Add a testimonial</h3>
        <label className="field-label mt-4">Customer name *</label>
        <input className="input-dark" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-testimonial-name" />
        <label className="field-label mt-4">Location / Parish</label>
        <input className="input-dark" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="admin-testimonial-location" />
        <label className="field-label mt-4">Review *</label>
        <textarea rows={4} className="input-dark resize-y" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="admin-testimonial-text" />
        <label className="field-label mt-4">Rating</label>
        <div className="relative">
          <select className="input-dark appearance-none pr-10" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} data-testid="admin-testimonial-rating">
            {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} star{r > 1 ? "s" : ""}</option>)}
          </select>
          <ChevronDown size={16} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-joell-yellow" />
        </div>
        {error && <p className="mt-4 text-sm text-red-300" role="alert">{error}</p>}
        <button type="submit" disabled={busy} className="btn-accent mt-6 w-full" data-testid="admin-testimonial-submit">
          {busy ? <Loader2 size={15} className="animate-spin" /> : "Add Testimonial"}
        </button>
      </form>

      <div className="space-y-3">
        {items.length === 0 && (
          <p className="border border-dashed border-white/20 p-10 text-center font-mono text-xs uppercase tracking-widest text-slate-500" data-testid="admin-testimonials-empty">No testimonials yet</p>
        )}
        {items.map((t) => (
          <div key={t.id} className="flex items-start justify-between gap-4 border border-white/10 bg-joell-surface p-5" data-testid={`admin-testimonial-${t.id}`}>
            <div>
              <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-joell-yellow">{t.name}{t.location && ` — ${t.location}`}</p>
              <p className="mt-2 text-sm text-slate-300">&ldquo;{t.text}&rdquo;</p>
              <p className="mt-2 text-xs text-slate-500">{t.rating} / 5 stars</p>
            </div>
            <button onClick={() => remove(t.id)} className="flex h-9 w-9 shrink-0 items-center justify-center border border-white/15 text-slate-400 transition-colors hover:border-red-500 hover:text-red-400" aria-label={`Delete testimonial from ${t.name}`} data-testid={`admin-testimonial-delete-${t.id}`}>
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function GalleryTab() {
  const qc = useQueryClient();
  const [mode, setMode] = useState("single");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Asphalt");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState("");
  const [result, setResult] = useState(null);
  const [files, setFiles] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [singleFile, setSingleFile] = useState(null);
  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);

  const { data: items = [] } = useQuery({
    queryKey: ["gallery"],
    queryFn: async () => (await axios.get(`${API}/gallery`)).data,
  });

  const add = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    if (mode === "multi") {
      const list = Array.from(files || []);
      if (!list.length) { setError("Please choose photo(s) first."); return; }
      if (list.length > 40) { setError("Up to 40 photos at a time — please split bigger batches."); return; }
      setBusy(true);
      let ok = 0;
      let failed = 0;
      for (let i = 0; i < list.length; i++) {
        setProgress(`Uploading ${i + 1} of ${list.length}…`);
        const cleanName = list[i].name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim();
        const itemTitle = title.trim() ? (i === 0 ? title.trim() : `${title.trim()} (${i + 1})`) : cleanName;
        const fd = new FormData();
        fd.append("title", itemTitle);
        fd.append("category", category);
        fd.append("is_before_after", "false");
        fd.append("image", list[i]);
        try {
          await axios.post(`${API}/gallery`, fd, authCfg);
          ok += 1;
        } catch {
          failed += 1;
        }
      }
      setBusy(false);
      setProgress("");
      setFiles(null);
      setTitle("");
      setResult({ ok, failed });
      qc.invalidateQueries({ queryKey: ["gallery"] });
      return;
    }
    if (mode === "album") {
      const list = Array.from(files || []);
      if (!title.trim()) { setError("Please give the project a name."); return; }
      if (!list.length) { setError("Please choose photo(s) first."); return; }
      if (list.length > 40) { setError("Up to 40 photos per album — please split bigger batches."); return; }
      setBusy(true);
      let album = null;
      try {
        const shell = new FormData();
        shell.append("title", title.trim());
        shell.append("category", category);
        shell.append("is_album", "true");
        const res = await axios.post(`${API}/gallery`, shell, authCfg);
        album = res.data;
        let ok = 0;
        let failed = 0;
        for (let i = 0; i < list.length; i++) {
          setProgress(`Uploading photo ${i + 1} of ${list.length}…`);
          const fd = new FormData();
          fd.append("image", list[i]);
          try {
            await axios.post(`${API}/gallery/${album.id}/photos`, fd, authCfg);
            ok += 1;
          } catch {
            failed += 1;
          }
        }
        if (!ok) {
          await axios.delete(`${API}/gallery/${album.id}`, authCfg);
          qc.invalidateQueries({ queryKey: ["gallery"] });
          setError("Upload failed — try again.");
          return;
        }
        setFiles(null);
        setTitle("");
        setResult({ ok, failed, album: true });
        qc.invalidateQueries({ queryKey: ["gallery"] });
      } catch (err) {
        if (album) {
          try { await axios.delete(`${API}/gallery/${album.id}`, authCfg); } catch {}
          qc.invalidateQueries({ queryKey: ["gallery"] });
        }
        setError(err.message === "photo" ? "Please choose photo(s) first." : "Upload failed — try again.");
      } finally {
        setBusy(false);
        setProgress("");
      }
      return;
    }
    if (mode === "video") {
      if (!title.trim()) { setError("Please give the video a name."); return; }
      if (!videoFile) { setError("Please choose a video first."); return; }
      if (videoFile.size > 200 * 1024 * 1024) { setError("Videos can be up to 200MB — please trim or compress longer clips."); return; }
      setBusy(true);
      let uploadId = null;
      try {
        const init = await axios.post(
          `${API}/videos/init`,
          { filename: videoFile.name, content_type: videoFile.type || "video/mp4", size: videoFile.size },
          authCfg
        );
        uploadId = init.data.id;
        const CHUNK = 4 * 1024 * 1024;
        const total = Math.max(1, Math.ceil(videoFile.size / CHUNK));
        for (let i = 0; i < total; i++) {
          const bytes = await videoFile.slice(i * CHUNK, Math.min((i + 1) * CHUNK, videoFile.size)).arrayBuffer();
          await axios.post(`${API}/videos/${uploadId}/chunk?idx=${i}`, bytes, {
            ...authCfg,
            headers: { ...(authCfg.headers || {}), "Content-Type": "application/octet-stream" },
          });
          setProgress(`Uploading video… ${Math.round(((i + 1) / total) * 100)}%`);
        }
        const fd = new FormData();
        fd.append("title", title.trim());
        fd.append("category", category);
        await axios.post(`${API}/videos/${uploadId}/finalize`, fd, authCfg);
        setVideoFile(null);
        setTitle("");
        setResult({ video: true });
        qc.invalidateQueries({ queryKey: ["gallery"] });
      } catch (err) {
        if (uploadId) {
          try { await axios.post(`${API}/videos/${uploadId}/cancel`, null, authCfg); } catch {}
        }
        setError(err.response?.data?.detail || "Upload failed — try again.");
      } finally {
        setBusy(false);
        setProgress("");
      }
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("category", category);
      if (mode === "single") {
        if (!singleFile) throw new Error("photo");
        fd.append("image", singleFile);
        fd.append("is_before_after", "false");
      } else {
        if (!beforeFile || !afterFile) throw new Error("photo");
        fd.append("before", beforeFile);
        fd.append("after", afterFile);
        fd.append("is_before_after", "true");
      }
      await axios.post(`${API}/gallery`, fd, authCfg);
      setTitle("");
      setSingleFile(null); setBeforeFile(null); setAfterFile(null);
      qc.invalidateQueries({ queryKey: ["gallery"] });
    } catch (err) {
      setError(err.message === "photo" ? "Please choose photo(s) first." : "Upload failed — try again.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await axios.delete(`${API}/gallery/${id}`, authCfg);
    qc.invalidateQueries({ queryKey: ["gallery"] });
  };

  return (
    <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_1.3fr]">
      <form onSubmit={add} className="h-fit border border-white/10 bg-joell-surface p-6" data-testid="admin-gallery-form">
        <h3 className="font-display text-lg font-bold uppercase text-white">Add project photos</h3>
        <div className="mt-4 grid grid-cols-2 gap-2">
          {[["single", "One photo"], ["multi", "Many photos"], ["album", "Project album"], ["video", "Video"], ["ba", "Before & after"]].map(([m, label]) => (
            <button key={m} type="button" onClick={() => { setMode(m); setError(""); setResult(null); setProgress(""); }}
              className={`border px-3 py-2.5 font-mono text-[10px] font-bold uppercase tracking-widest ${mode === m ? "border-joell-yellow bg-joell-yellow/10 text-joell-yellow" : "border-white/15 text-slate-400"}`}
              data-testid={`admin-gallery-mode-${m}`}>
              {label}
            </button>
          ))}
        </div>
        <label className="field-label mt-4">{mode === "multi" ? "Batch title (optional)" : "Project title *"}</label>
        <input className="input-dark" placeholder="e.g. Driveway resurfacing, Paget" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="admin-gallery-title" />
        <label className="field-label mt-4">Category *</label>
        <div className="relative">
          <select className="input-dark appearance-none pr-10" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="admin-gallery-category">
            {GALLERY_CATEGORIES.filter((c) => c !== "All").map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <ChevronDown size={16} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-joell-yellow" />
        </div>
        {mode === "single" ? (
          <>
            <label className="field-label mt-4">Photo *</label>
            <input type="file" accept="image/*" onChange={(e) => setSingleFile(e.target.files?.[0] || null)} className="input-dark !py-2.5 text-xs" data-testid="admin-gallery-single-file" />
          </>
        ) : mode === "multi" ? (
          <>
            <label className="field-label mt-4">Photos * (pick as many as you like)</label>
            <input type="file" accept="image/*" multiple onClick={(e) => { e.target.value = null; setFiles(null); }} onChange={(e) => setFiles(e.target.files || null)} className="input-dark !py-2.5 text-xs" data-testid="admin-gallery-multi-file" />
            {files && files.length > 0 && (
              <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-joell-yellow" data-testid="admin-gallery-multi-count">{files.length} photo{files.length === 1 ? "" : "s"} selected</p>
            )}
          </>
        ) : mode === "album" ? (
          <>
            <label className="field-label mt-4">Album photos * (pick as many as you like)</label>
            <input type="file" accept="image/*" multiple onClick={(e) => { e.target.value = null; setFiles(null); }} onChange={(e) => setFiles(e.target.files || null)} className="input-dark !py-2.5 text-xs" data-testid="admin-gallery-album-file" />
            {files && files.length > 0 && (
              <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-joell-yellow" data-testid="admin-gallery-album-count">{files.length} photo{files.length === 1 ? "" : "s"} selected</p>
            )}
          </>
        ) : mode === "video" ? (
          <>
            <label className="field-label mt-4">Video file * (MP4 / MOV / WebM, up to 200MB)</label>
            <input
              type="file"
              accept="video/mp4,video/quicktime,video/webm,video/x-msvideo,video/x-matroska,video/*"
              onClick={(e) => { e.target.value = null; }}
              onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
              className="input-dark !py-2.5 text-xs"
              data-testid="admin-gallery-video-file"
            />
            {videoFile && (
              <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-joell-yellow" data-testid="admin-gallery-video-name">
                {videoFile.name} — {(videoFile.size / (1024 * 1024)).toFixed(1)}MB
              </p>
            )}
          </>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="field-label mt-4">Before *</label>
              <input type="file" accept="image/*" onChange={(e) => setBeforeFile(e.target.files?.[0] || null)} className="input-dark !py-2.5 text-xs" data-testid="admin-gallery-before-file" />
            </div>
            <div>
              <label className="field-label mt-4">After *</label>
              <input type="file" accept="image/*" onChange={(e) => setAfterFile(e.target.files?.[0] || null)} className="input-dark !py-2.5 text-xs" data-testid="admin-gallery-after-file" />
            </div>
          </div>
        )}
        {error && <p className="mt-4 text-sm text-red-300" role="alert">{error}</p>}
        {progress && <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-joell-yellow" data-testid="admin-gallery-progress">{progress}</p>}
        {result && (
          <p className="mt-4 text-sm text-joell-yellow" data-testid="admin-gallery-result">
            {result.video
              ? "Video added to Our Work."
              : `Added ${result.ok} photo${result.ok === 1 ? "" : "s"}${result.album ? " to the album" : " to Our Work"}${result.failed ? ` — ${result.failed} failed, please retry those` : ""}.`}
          </p>
        )}
        <button type="submit" disabled={busy} className="btn-accent mt-6 w-full" data-testid="admin-gallery-submit">
          {busy ? <Loader2 size={15} className="animate-spin" /> : mode === "multi" ? "Add All to Our Work" : mode === "album" ? "Create Album" : mode === "video" ? "Add Video" : "Add to Our Work"}
        </button>
      </form>

      <div className="space-y-3">
        {items.length === 0 && (
          <p className="border border-dashed border-white/20 p-10 text-center font-mono text-xs uppercase tracking-widest text-slate-500" data-testid="admin-gallery-empty">No gallery items yet</p>
        )}
        {items.map((item) => (
          <div key={item.id} className="flex items-center gap-4 border border-white/10 bg-joell-surface p-4" data-testid={`admin-gallery-item-${item.id}`}>
            <div className="flex w-24 shrink-0 gap-1">
              {item.is_video ? (
                <video src={videoSrc(item)} muted playsInline preload="metadata" className="aspect-square w-full object-cover" />
              ) : item.is_before_after ? (
                <>
                  <img src={gallerySrc(item, "before")} alt="before" className="aspect-square w-1/2 object-cover" loading="lazy" />
                  <img src={gallerySrc(item, "after")} alt="after" className="aspect-square w-1/2 object-cover" loading="lazy" />
                </>
              ) : (
                <img src={gallerySrc(item)} alt={item.title} className="aspect-square w-full object-cover" loading="lazy" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-display text-sm font-bold uppercase text-white">{item.title}</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-joell-yellow">{item.category}{item.is_video ? " • video" : item.is_before_after ? " • before/after" : item.is_album ? ` • album — ${(item.album_paths || []).length} photos` : ""}</p>
            </div>
            <button onClick={() => remove(item.id)} className="flex h-9 w-9 shrink-0 items-center justify-center border border-white/15 text-slate-400 transition-colors hover:border-red-500 hover:text-red-400" aria-label={`Delete ${item.title}`} data-testid={`admin-gallery-delete-${item.id}`}>
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function HoursTab() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["hours"],
    queryFn: async () => (await axios.get(`${API}/hours`)).data,
  });
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const current = rows !== null ? rows : (data?.rows?.length ? data.rows : []);

  const setRow = (i, key, value) => {
    setSaved(false);
    setRows(current.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));
  };
  const addRow = () => {
    setSaved(false);
    setRows([...current, { days: "", time: "" }]);
  };
  const removeRow = (i) => {
    setSaved(false);
    setRows(current.filter((_, idx) => idx !== i));
  };
  const save = async () => {
    setBusy(true);
    try {
      const { data: savedData } = await axios.put(`${API}/hours`, { rows: current }, authCfg);
      setRows(savedData.rows);
      qc.invalidateQueries({ queryKey: ["hours"] });
      setSaved(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 max-w-2xl">
      <div className="border border-white/10 bg-joell-surface p-6" data-testid="admin-hours-form">
        <h3 className="font-display text-lg font-bold uppercase text-white">Business hours</h3>
        <p className="mt-2 text-sm text-slate-400">These hours appear on the Contact page. Edit them any time — e.g. for seasonal changes.</p>
        <div className="mt-6 space-y-3">
          {current.map((row, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2" data-testid={`admin-hours-row-${i}`}>
              <input className="input-dark" placeholder="Days (e.g. Monday – Friday)" value={row.days} onChange={(e) => setRow(i, "days", e.target.value)} data-testid={`admin-hours-days-${i}`} />
              <input className="input-dark" placeholder="Hours (e.g. 7:00 AM – 6:00 PM)" value={row.time} onChange={(e) => setRow(i, "time", e.target.value)} data-testid={`admin-hours-time-${i}`} />
              <button onClick={() => removeRow(i)} className="flex h-11 w-11 items-center justify-center border border-white/15 text-slate-400 transition-colors hover:border-red-500 hover:text-red-400" aria-label={`Remove row ${i + 1}`} data-testid={`admin-hours-remove-${i}`}>
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button onClick={addRow} className="btn-outline !px-4 !py-2.5" data-testid="admin-hours-add-row">
            <Plus size={14} /> Add Row
          </button>
          <button onClick={save} disabled={busy || current.length === 0} className="btn-accent mt-0 w-auto !px-6 !py-2.5 disabled:opacity-60" data-testid="admin-hours-save">
            {busy ? <Loader2 size={15} className="animate-spin" /> : "Save Hours"}
          </button>
          {saved && <span className="font-mono text-[11px] font-bold uppercase tracking-widest text-joell-yellow" data-testid="admin-hours-saved">Saved — live on the Contact page</span>}
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  usePageTitle("Owner Dashboard");
  const qc = useQueryClient();
  const [tab, setTab] = useState("quotes");

  const { data: me, isLoading } = useQuery({
    queryKey: ["me"],
    queryFn: async () => (await axios.get(`${API}/auth/me`, authCfg)).data,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-joell-black">
        <Loader2 size={28} className="animate-spin text-joell-yellow" />
      </div>
    );
  }
  if (!me) return <Login onLogin={(user) => qc.setQueryData(["me"], user)} />;

  return (
    <div className="min-h-screen bg-joell-black pb-24" data-testid="admin-dashboard">
      <div className="border-b border-white/10 bg-joell-coal/60">
        <div className="container-x flex flex-wrap items-center justify-between gap-4 py-8">
          <div>
            <Eyebrow>JOELL Owner Dashboard</Eyebrow>
            <h1 className="mt-2 font-display text-2xl font-bold uppercase text-white">Manage your website</h1>
          </div>
          <button
            onClick={async () => { await axios.post(`${API}/auth/logout`, {}, authCfg); qc.setQueryData(["me"], null); }}
            className="btn-outline !px-4 !py-2.5"
            data-testid="admin-logout-button"
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
        <div className="container-x flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-5 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.15em] transition-colors ${
                tab === t.id ? "border-joell-yellow text-joell-yellow" : "border-transparent text-slate-400 hover:text-white"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="container-x py-8">
        {tab === "quotes" && <QuotesTab />}
        {tab === "applications" && <ApplicationsTab />}
        {tab === "jobs" && <JobsTab />}
        {tab === "testimonials" && <TestimonialsTab />}
        {tab === "gallery" && <GalleryTab />}
        {tab === "hours" && <HoursTab />}
      </div>
    </div>
  );
}
